// src/hooks/useMessage.ts
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageType } from '@/components/ui/Message';

interface MessageState {
  type: MessageType;
  content: string | null;
  translateKey: string | null;
  values?: Record<string, string | number>;
  visible: boolean;
  autoHideTime?: number | null;
}

interface UseMessageReturn {
  message: MessageState;
  showMessage: (type: MessageType, content: string | null, options?: {
    translateKey?: string | null;
    values?: Record<string, string | number>;
    autoHide?: boolean;
    autoHideTime?: number;
  }) => void;
  hideMessage: () => void;
  isVisible: boolean;
}

const defaultAutoHideTime = 5000; // 5 секунд

/**
 * Хук для централизованного управления сообщениями (уведомления, ошибки, информационные сообщения)
 */
export const useMessage = (): UseMessageReturn => {
  const { t } = useTranslation();
  const [message, setMessage] = useState<MessageState>({
    type: 'info',
    content: null,
    translateKey: null,
    visible: false,
    autoHideTime: null,
    values: {}
  });

  // Идентификатор таймера автоскрытия
  const [autoHideTimer, setAutoHideTimer] = useState<NodeJS.Timeout | null>(null);

  /**
   * Скрывает сообщение
   */
  const hideMessage = useCallback(() => {
    setMessage(prev => ({ ...prev, visible: false }));
    
    // Очищаем таймер, если он существует
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
      setAutoHideTimer(null);
    }
  }, [autoHideTimer]);

  /**
   * Показывает сообщение определенного типа
   * @param type Тип сообщения ('error', 'success', 'info', 'warning', 'debug')
   * @param content Текст сообщения (если не используется перевод)
   * @param options Дополнительные настройки
   */
  const showMessage = useCallback((
    type: MessageType,
    content: string | null,
    options?: {
      translateKey?: string | null;
      values?: Record<string, string | number>;
      autoHide?: boolean;
      autoHideTime?: number;
    }
  ) => {
    // Очищаем предыдущий таймер, если он существует
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
      setAutoHideTimer(null);
    }

    // Проверяем, если контент пустой и нет ключа перевода, используем ключ по умолчанию по типу
    const finalTranslateKey = options?.translateKey || (content ? null : `common.${type}`);
    const finalContent = content || (finalTranslateKey ? t(finalTranslateKey, options?.values) : null);

    // Устанавливаем новое сообщение
    setMessage({
      type,
      content: finalContent,
      translateKey: finalTranslateKey,
      visible: true,
      autoHideTime: options?.autoHide === false ? null : (options?.autoHideTime || defaultAutoHideTime),
      values: options?.values || {}
    });

    // Устанавливаем таймер автоскрытия, если это необходимо
    if (options?.autoHide !== false) {
      const timer = setTimeout(
        hideMessage,
        options?.autoHideTime || defaultAutoHideTime
      );
      setAutoHideTimer(timer);
    }
  }, [hideMessage, autoHideTimer, t]);

  return {
    message,
    showMessage,
    hideMessage,
    isVisible: message.visible
  };
};

export default useMessage;